import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, NavLink } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { SocieteProvider } from "./contexts/SocieteContext";
import { SecretModeProvider } from "./contexts/SecretModeContext";
import { useBranding } from "./hooks/useBranding";
import { Sidebar } from "./components/Sidebar";
import QuotaBanner from "./components/QuotaBanner";
import LiteConfigPage from "./pages/LiteConfigPage";
import GroupConsolidationPage from "./pages/GroupConsolidationPage";
import { LiteModeProvider } from "./contexts/LiteModeContext";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { SocieteScopeBanner } from "./components/SocieteScopeBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import HeaderThemeToggle from "./components/HeaderThemeToggle";
import VersionUpdateBanner from "./components/VersionUpdateBanner";
import { UserLookupProvider } from "./components/UserDisplay";
import { Menu, X, Bell } from "lucide-react";
import LoginPage from "./pages/LoginPage";
import BrandThemeApplier from "./components/BrandThemeApplier";
import DownloadsPage from "./pages/DownloadsPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import ServicesPage from "./pages/ServicesPage";
import AuditsPage from "./pages/AuditsPage";
import CloudDocumentsPage from "./pages/CloudDocumentsPage";
import AdminPage from "./pages/AdminPage";
import AccompagnementPage from "./pages/AccompagnementPage";
import AdminClientDetail from "./pages/AdminClientDetail";
import AdminAccompagnementDetail from "./pages/AdminAccompagnementDetail";
import FormationLMSPage from "./pages/FormationLMSPage";
import FormationBuilderPage from "./pages/FormationBuilderPage";
import CourseModulesPage from "./pages/CourseModulesPage";
import CourseModuleEditorPage from "./pages/CourseModuleEditorPage";
import AppInstallPage, { AppLauncher } from "./pages/AppInstallPage";
import { DocTemplatesListPage, DocTemplateEditorPage } from "./pages/DocTemplateBuilderPage";
import SessionsPage from "./pages/SessionsPage";
import SessionPilotagePage from "./pages/SessionPilotagePage";
import PublicSignPage from "./pages/PublicSignPage";
import PublicQuizPage from "./pages/PublicQuizPage";
import PunchPage from "./pages/PunchPage";
import InscriptionsDashboardPage from "./pages/InscriptionsDashboardPage";
import StatsBpfPage from "./pages/StatsBpfPage";
import FormateurSessionsPage from "./pages/FormateurSessionsPage";
import FormateurSessionDetailPage from "./pages/FormateurSessionDetailPage";
import MesFormationsPage from "./pages/MesFormationsPage";
import MesFormationDetailPage from "./pages/MesFormationDetailPage";
// EvaluationGroupsPage was removed in iter_135 (module deleted)
import StudentPortalPage from "./pages/StudentPortalPage";
import AdminAuditsPage from "./pages/AdminAuditsPage";
import BibliothequePage from "./pages/BibliothequePage";
import CertificationsPage from "./pages/CertificationsPage";
import CandidatParcoursPage from "./pages/CandidatParcoursPage";
import VerifyParcheminPage from "./pages/VerifyParcheminPage";
import MesParcoursCertifiantsPage from "./pages/MesParcoursCertifiantsPage";
import PPFConfigPage from "./pages/PPFConfigPage";
import PWAPrompt from "./components/PWAPrompt";
import { Toaster } from "./components/ui/sonner";
import ProgramDetailPage from "./pages/ProgramDetailPage";
import ProgramEditPage from "./pages/ProgramEditPage";
import EvaluationsPage from "./pages/EvaluationsPage";
import EvaluationRespondPage from "./pages/EvaluationRespondPage";
import CataloguePage from "./pages/CataloguePage";
import AdminCatalogueLeadsPage from "./pages/AdminCatalogueLeadsPage";
import OnboardingPage from "./pages/OnboardingPage";
import AgendaPage from "./pages/AgendaPage";
import FicheRenseignementPage from "./pages/FicheRenseignementPage";
import FinancialPage from "./pages/FinancialPage";
import BuilderPage from "./pages/BuilderPage";
import TeamPage from "./pages/TeamPage";
import StaffProfilePage from "./pages/StaffProfilePage";
import MessagesPage from "./pages/MessagesPage";
import PrevisionnelPage from "./pages/PrevisionnelPage";
import ResultatCandidatPage from "./pages/ResultatCandidatPage";
import AccountingPage from "./pages/AccountingPage";
import ConsolidatedAccountingPage from "./pages/ConsolidatedAccountingPage";
import SocialPage from "./pages/SocialPage";
import MyPayslipsPage from "./pages/MyPayslipsPage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import ClubPage from "./pages/ClubPage";
import FacturationPage from "./pages/FacturationPage";
import CrmPage from "./pages/CrmPage";
import QualiopiPage from "./pages/QualiopiPage";
import MarketingPage from "./pages/MarketingPage";
import MarketingHubPage from "./pages/MarketingHubPage";
import MarketingGooglePage from "./pages/MarketingGooglePage";
import AffiliatePage from "./pages/AffiliatePage";
import HelpCenterPage from "./pages/HelpCenterPage";
import MailingPage from "./pages/MailingPage";
import MarketingStudioPage from "./pages/MarketingStudioPage";
import ProspectPage from "./pages/ProspectPage";
import ProspectDetailPage from "./pages/ProspectDetailPage";
import ExamCertifPage from "./pages/ExamCertifPage";
import ExamSessionPage from "./pages/ExamSessionPage";
import OpportunityDetailPage from "./pages/OpportunityDetailPage";
import HotLeadsPage from "./pages/HotLeadsPage";
import CallTranscriptionPage from "./pages/CallTranscriptionPage";
import SecurityAlertsPage from "./pages/SecurityAlertsPage";
import AccessAuditPage from "./pages/AccessAuditPage";
import AccountSecurityPage from "./pages/AccountSecurityPage";
import TwoFactorGate from "./components/security/TwoFactorGate";
import IntegrationsHealthPage from "./pages/IntegrationsHealthPage";
import AdminPunchPage from "./pages/AdminPunchPage";
import PreflightCheckPage from "./pages/PreflightCheckPage";
import MyPunchPage from "./pages/MyPunchPage";
import {
  InventoryDashboardPage,
  ProductCatalogPage,
  SuppliersPage,
  PurchaseOrdersPage,
  CustomerOrdersPage,
  RestockSuggestionsPage,
  ShopifyConfigPage,
} from "./pages/inventory";
import MonEspaceHomePage from "./pages/MonEspaceHomePage";
import FirstPasswordGate from "./components/FirstPasswordGate";
import SecurityMonitor from "./components/security/SecurityMonitor";
import WorkflowsPage from "./pages/WorkflowsPage";
import WorkflowEditorPage from "./pages/WorkflowEditorPage";
import UnifiedInboxPage from "./pages/UnifiedInboxPage";
import MessagingStatsPage from "./pages/MessagingStatsPage";
import PublicQuestionnairePage from "./pages/PublicQuestionnairePage";
import TenantTeamPage from "./pages/TenantTeamPage";
import MemberProfilePage from "./pages/MemberProfilePage";
import TeamAccessPage from "./pages/TeamAccessPage";
import SocietePermissionsPage from "./pages/SocietePermissionsPage";
import PermissionsAuditPage from "./pages/PermissionsAuditPage";
import AircallTranslatorPage from "./pages/AircallTranslatorPage";
import CommercialPanelPage from "./pages/CommercialPanelPage";
import ObjectionCookbookPage from "./pages/ObjectionCookbookPage";
import ManagerCoachStatsPage from "./pages/ManagerCoachStatsPage";
import TenantSettingsPage from "./pages/TenantSettingsPage";
import BrandingSettingsPage from "./pages/BrandingSettingsPage";
import OutilsPage from "./pages/OutilsPage";
import ImportPage from "./pages/ImportPage";
import DepensesPage from "./pages/DepensesPage";
import PersonalSpacePage from "./pages/PersonalSpacePage";
// PersonalDocumentsPage merged into CloudDocumentsPage as scope="personal"
import PersonalEquipmentsPage from "./pages/PersonalEquipmentsPage";
import AdminServicesPage from "./pages/AdminServicesPage";
import EncaissementsPage from "./pages/EncaissementsPage";
import AgentIAPage from "./pages/AgentIAPage";
import PostesPage from "./pages/PostesPage";
import TresoreriePage from "./pages/TresoreriePage";
import HoldingPage from "./pages/HoldingPage";
import SocietesPage from "./pages/SocietesPage";
import SocieteBuilderPage from "./pages/SocieteBuilderPage";
import PartnersAdminPage from "./pages/PartnersAdminPage";
import PartnerAcceptPage from "./pages/PartnerAcceptPage";
import PartnerLayout from "./pages/partner/PartnerLayout";
import PartnerHome from "./pages/partner/PartnerHome";
import PartnerOrders, { PartnerInvoices, PartnerCatalogue } from "./pages/partner/PartnerOrders";
import { PartnerMissions, PartnerLeads, PartnerCommissions, PartnerMessages, PartnerDocuments } from "./pages/partner/PartnerModules";
import PartnerLms from "./pages/partner/PartnerLms";
import AnalyticsPage from "./pages/AnalyticsPage";
import NotFoundPage from "./pages/NotFoundPage";
import ObservabilityPage from "./pages/ObservabilityPage";
import SalarieHomePage from "./pages/SalarieHomePage";
import KioskPage from "./pages/KioskPage";
import SalarieLayout from "./pages/salarie/SalarieLayout";
import SalarieSettingsPage from "./pages/salarie/SalarieSettingsPage";
import SalarieEspacePage from "./pages/salarie/SalarieEspacePage";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-portal-252/artifacts/ymujr84m_Logo%20blitzz%20%2B%20BYB%20ok%20sans%20fond%20copie.png";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  if (mobileMenuOpen && location.pathname) {
    // Will close on next render via effect below
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: isDark ? "#0b0811" : "#f5f5f7" }}>
        <div className="text-center">
          <img src={LOGO_URL} alt="Blitzz" className="h-14 mx-auto mb-4 animate-pulse object-contain" />
          <p className="text-sm" style={{ color: isDark ? "#b0b8c9" : "#6b7280" }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Iter 352 — apps de bureau brandées : page de connexion de la société (window.blitzzDesktop.loginSlug)
    const slug = typeof window !== "undefined" && window.blitzzDesktop?.loginSlug;
    return <Navigate to={slug ? `/login/${slug}` : "/login"} state={{ from: location }} replace />;
  }

  // Iter 277 — comptes à mot de passe temporaire : changement obligatoire avant tout accès
  if (user.must_change_password) return <FirstPasswordGate />;
  if (user.twofa_setup_required) return <TwoFactorGate />;

  // Iter 320 — partenaires : panel dédié (sans Sidebar), sauf l'espace formateur pour les formateurs externes
  if (user.role === "partner") {
    if (location.pathname.startsWith("/partner")) return children;
    if (location.pathname.startsWith("/formateur")) {
      return <PartnerLayout>{children}</PartnerLayout>;
    }
    return <Navigate to="/partner" replace />;
  }

  return (
    <div className="flex h-screen" style={{ background: isDark ? "#0b0811" : "#f5f5f7" }}>
      {/* Mobile header */}
      <div className="mobile-app-header md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-3 pb-2.5 pt-[calc(0.625rem+env(safe-area-inset-top))] border-b border-white/10 backdrop-blur-lg" style={{ background: isDark ? "rgba(15,11,23,0.95)" : "rgba(255,255,255,0.95)" }}>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors" data-testid="mobile-menu-toggle">
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <img src={LOGO_URL} alt="Blitzz" className="h-7 object-contain" />
        <NavLink to="/notifications" className="p-2 rounded-lg text-slate-400 hover:text-white relative" data-testid="mobile-notif-btn">
          <Bell className="w-5 h-5" />
        </NavLink>
      </div>

      {/* Sidebar overlay for mobile */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <div className={`mobile-app-drawer md:hidden fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] transition-transform duration-300 ease-out h-[100dvh] ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"}`} style={{ background: isDark ? "#0f0b17" : "#ffffff" }}>
        <Sidebar onNavigate={() => setMobileMenuOpen(false)} />
      </div>

      {/* Main content */}
      <main className="mobile-app-main flex-1 overflow-auto w-full md:pt-0 pt-[calc(3rem+env(safe-area-inset-top))]">
        <HeaderThemeToggle />
        <VersionUpdateBanner />
        <ImpersonationBanner />
        <QuotaBanner />
        <div className="px-3 sm:px-4 lg:px-6 mt-2"><SocieteScopeBanner /></div>
        {children}
      </main>
    </div>
  );
};

const AdminRoute = ({ section }) => {
  const { isAdmin, isStaff, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin && !isStaff) return <Navigate to="/dashboard" replace />;
  return <AdminPage section={section} />;
};

const AdminRouteWrapper = ({ children }) => {
  const { isAdmin, isStaff, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin && !isStaff) return <Navigate to="/dashboard" replace />;
  return children;
};

const SuperadminRoute = ({ children }) => {
  const { isSuperadmin, loading } = useAuth();
  if (loading) return null;
  if (!isSuperadmin) return <Navigate to="/dashboard" replace />;
  return children;
};

// Staff Blitzz uniquement (admin/superadmin sans tenant) — ex: Pipeline Cegid (gestion de la compta des tenants)
const BlitzzAdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

// Iter 246.2 — When a tenant_admin/tenant_member clicks "Équipe" (legacy /admin/team),
// redirect them to the new /tenant/team page that hosts the permissions panel.
function TeamPageWithRedirect() {
  const { user } = useAuth();
  if (user?.role === "tenant_admin" || user?.role === "tenant_member") {
    return <Navigate to="/tenant/team" replace />;
  }
  return <TeamPage />;
}


function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <ErrorBoundary resetKey={location.pathname}>
    <SecurityMonitor />
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/:slug" element={<LoginPage />} />
      <Route path="/telechargements" element={<DownloadsPage />} />
      <Route path="/telechargements/:slug" element={<DownloadsPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/accompagnement" element={<ProtectedRoute><AccompagnementPage /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
      <Route path="/formations" element={<ProtectedRoute><StudentPortalPage /></ProtectedRoute>} />
      <Route path="/audits" element={<ProtectedRoute><AuditsPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><CloudDocumentsPage /></ProtectedRoute>} />
      <Route path="/agenda" element={<ProtectedRoute><AgendaPage /></ProtectedRoute>} />
      <Route path="/mon-espace" element={<ProtectedRoute><MonEspaceHomePage /></ProtectedRoute>} />
      <Route path="/mon-espace/taches" element={<ProtectedRoute><PersonalSpacePage defaultTab="tasks" /></ProtectedRoute>} />
      <Route path="/mon-espace/equipement" element={<ProtectedRoute><PersonalEquipmentsPage /></ProtectedRoute>} />
      <Route path="/mon-espace/depenses" element={<ProtectedRoute><PersonalSpacePage defaultTab="expenses" /></ProtectedRoute>} />
      <Route path="/mon-espace/documents" element={<ProtectedRoute><CloudDocumentsPage initialScope="personal" hideScopeTabs /></ProtectedRoute>} />
      <Route path="/mon-espace/bulletins" element={<ProtectedRoute><MyPayslipsPage /></ProtectedRoute>} />
      <Route path="/fiche-renseignement" element={<ProtectedRoute><FicheRenseignementPage /></ProtectedRoute>} />
      {/* Admin */}
      <Route path="/admin/clients" element={<ProtectedRoute><AdminRoute section="clients" /></ProtectedRoute>} />
      <Route path="/admin/clients/:userId" element={<ProtectedRoute><AdminClientDetail /></ProtectedRoute>} />
      <Route path="/admin/accompagnements" element={<ProtectedRoute><AdminRoute section="accompagnements" /></ProtectedRoute>} />
      <Route path="/admin/accompagnements/:accId" element={<ProtectedRoute><AdminAccompagnementDetail /></ProtectedRoute>} />
      <Route path="/admin/services" element={<ProtectedRoute><AdminRouteWrapper><AdminServicesPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/previsionnel" element={<ProtectedRoute><AdminRouteWrapper><PrevisionnelPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/master" element={<Navigate to="/dashboard" replace />} />
      <Route path="/admin/configuration" element={<ProtectedRoute><AdminRouteWrapper><LiteConfigPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/groupe" element={<ProtectedRoute><AdminRouteWrapper><GroupConsolidationPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/bibliotheque" element={<ProtectedRoute><AdminRouteWrapper><BibliothequePage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/builder" element={<ProtectedRoute><AdminRouteWrapper><FormationBuilderPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/modules" element={<ProtectedRoute><AdminRouteWrapper><CourseModulesPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/modules/:module_id" element={<ProtectedRoute><AdminRouteWrapper><CourseModuleEditorPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/lecons" element={<Navigate to="/admin/formation/modules" replace />} />
      <Route path="/admin/formation/app" element={<ProtectedRoute><AppInstallPage /></ProtectedRoute>} />
      <Route path="/app" element={<AppLauncher />} />
      <Route path="/admin/formation/doc-templates" element={<ProtectedRoute><AdminRouteWrapper><DocTemplatesListPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/doc-templates/:template_id" element={<ProtectedRoute><AdminRouteWrapper><DocTemplateEditorPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/sessions" element={<ProtectedRoute><AdminRouteWrapper><SessionsPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/sessions/:session_id" element={<ProtectedRoute><AdminRouteWrapper><SessionPilotagePage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/inscriptions" element={<ProtectedRoute><AdminRouteWrapper><InscriptionsDashboardPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/bpf" element={<ProtectedRoute><AdminRouteWrapper><StatsBpfPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/certifications" element={<ProtectedRoute><AdminRouteWrapper><CertificationsPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/examens" element={<ProtectedRoute><AdminRouteWrapper><ExamCertifPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/examens/session/:sessionId" element={<ProtectedRoute><AdminRouteWrapper><ExamSessionPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/mon-parcours-certifiant/:candidate_id" element={<ProtectedRoute><CandidatParcoursPage /></ProtectedRoute>} />
      <Route path="/mes-parcours-certifiants" element={<ProtectedRoute><MesParcoursCertifiantsPage /></ProtectedRoute>} />
      <Route path="/admin/ppf/config" element={<ProtectedRoute><AdminRouteWrapper><PPFConfigPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/verify/:parchemin_id" element={<VerifyParcheminPage />} />
      {/* /admin/formation/evaluation-groups route removed in iter_135 (module deleted) */}
      <Route path="/formateur/sessions" element={<ProtectedRoute><FormateurSessionsPage /></ProtectedRoute>} />
      <Route path="/formateur/sessions/:session_id" element={<ProtectedRoute><FormateurSessionDetailPage /></ProtectedRoute>} />
      <Route path="/formateur/sessions/:session_id/admin" element={<ProtectedRoute><SessionPilotagePage /></ProtectedRoute>} />
      <Route path="/mes-formations" element={<ProtectedRoute><MesFormationsPage /></ProtectedRoute>} />
      <Route path="/mes-formations/:session_id" element={<ProtectedRoute><MesFormationDetailPage /></ProtectedRoute>} />
      <Route path="/resultat/:token" element={<ResultatCandidatPage />} />
      <Route path="/sign/:token" element={<PublicSignPage />} />
      <Route path="/quiz/:token" element={<PublicQuizPage />} />
      <Route path="/punch" element={<PunchPage />} />
      <Route path="/borne/:terminalId" element={<KioskPage />} />
      <Route path="/admin/formation/bibliotheque/:program_id" element={<ProtectedRoute><AdminRouteWrapper><ProgramDetailPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/bibliotheque/:program_id/edit" element={<ProtectedRoute><AdminRouteWrapper><ProgramEditPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formation/evaluations" element={<ProtectedRoute><AdminRouteWrapper><EvaluationsPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/evaluation/:send_id" element={<EvaluationRespondPage />} />
      <Route path="/catalogue" element={<CataloguePage />} />
      <Route path="/admin/catalogue-leads" element={<ProtectedRoute><AdminRouteWrapper><AdminCatalogueLeadsPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/formations" element={<ProtectedRoute><AdminRouteWrapper><FormationLMSPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/audits" element={<ProtectedRoute><AdminRouteWrapper><AdminAuditsPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/documents" element={<ProtectedRoute><CloudDocumentsPage /></ProtectedRoute>} />
      <Route path="/admin/agenda" element={<ProtectedRoute><AgendaPage /></ProtectedRoute>} />
      <Route path="/admin/financial" element={<ProtectedRoute><FinancialPage /></ProtectedRoute>} />
      <Route path="/admin/builder" element={<ProtectedRoute><BuilderPage /></ProtectedRoute>} />
      <Route path="/admin/team" element={<ProtectedRoute><TeamPageWithRedirect /></ProtectedRoute>} />
      <Route path="/admin/staff/:userId/profile" element={<ProtectedRoute><StaffProfilePage /></ProtectedRoute>} />
      <Route path="/staff/my-profile" element={<ProtectedRoute><StaffProfilePage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/admin/comptabilite" element={<Navigate to="/admin/comptabilite/generale" replace />} />
      <Route path="/admin/comptabilite/generale" element={<ProtectedRoute><AdminRouteWrapper><AccountingPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/comptabilite/consolide" element={<ProtectedRoute><AdminRouteWrapper><ConsolidatedAccountingPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/comptabilite" element={<Navigate to="/" replace />} />
      <Route path="/admin/social" element={<ProtectedRoute><SocialPage /></ProtectedRoute>} />
      <Route path="/admin/club" element={<ProtectedRoute><ClubPage /></ProtectedRoute>} />
      <Route path="/admin/facturation" element={<ProtectedRoute><FacturationPage /></ProtectedRoute>} />
      <Route path="/admin/crm" element={<ProtectedRoute><CrmPage /></ProtectedRoute>} />
      <Route path="/admin/leads-chauds" element={<ProtectedRoute><HotLeadsPage /></ProtectedRoute>} />
      <Route path="/admin/aircall/calls/:callLogId" element={<ProtectedRoute><CallTranscriptionPage /></ProtectedRoute>} />
      <Route path="/admin/security" element={<ProtectedRoute><SecurityAlertsPage /></ProtectedRoute>} />
      <Route path="/admin/security/audit" element={<ProtectedRoute><AccessAuditPage /></ProtectedRoute>} />
      <Route path="/securite" element={<ProtectedRoute><AccountSecurityPage /></ProtectedRoute>} />
      <Route path="/admin/integrations-health" element={<ProtectedRoute><IntegrationsHealthPage /></ProtectedRoute>} />
      <Route path="/admin/punch" element={<ProtectedRoute><AdminPunchPage /></ProtectedRoute>} />
      <Route path="/admin/preflight" element={<ProtectedRoute><PreflightCheckPage /></ProtectedRoute>} />
      <Route path="/admin/ai-agents" element={<Navigate to="/admin/agent-ia" replace />} />
      <Route path="/admin/ai-agents/:agentType" element={<Navigate to="/admin/agent-ia" replace />} />
      <Route path="/mon-espace/whatsapp" element={<Navigate to="/mon-espace/pointages" replace />} />
      <Route path="/mon-espace/pointages" element={<ProtectedRoute><MyPunchPage /></ProtectedRoute>} />
      {/* Iter 245 — Module Commandes & Stocks */}
      <Route path="/admin/inventory" element={<ProtectedRoute><InventoryDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/inventory/products" element={<ProtectedRoute><ProductCatalogPage /></ProtectedRoute>} />
      <Route path="/admin/inventory/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />
      <Route path="/admin/inventory/purchase-orders" element={<ProtectedRoute><PurchaseOrdersPage /></ProtectedRoute>} />
      <Route path="/admin/inventory/customer-orders" element={<ProtectedRoute><CustomerOrdersPage /></ProtectedRoute>} />
      <Route path="/admin/inventory/restock" element={<ProtectedRoute><RestockSuggestionsPage /></ProtectedRoute>} />
      <Route path="/admin/inventory/shopify" element={<ProtectedRoute><ShopifyConfigPage /></ProtectedRoute>} />
      <Route path="/admin/workflows" element={<ProtectedRoute><WorkflowsPage /></ProtectedRoute>} />
      <Route path="/admin/workflows/:workflowId" element={<ProtectedRoute><WorkflowEditorPage /></ProtectedRoute>} />
      <Route path="/admin/messagerie" element={<ProtectedRoute><UnifiedInboxPage /></ProtectedRoute>} />
      <Route path="/admin/messagerie/stats" element={<ProtectedRoute><MessagingStatsPage /></ProtectedRoute>} />
      <Route path="/admin/opportunite/:oppId" element={<ProtectedRoute><OpportunityDetailPage /></ProtectedRoute>} />
      <Route path="/admin/prospects" element={<ProtectedRoute><ProspectPage /></ProtectedRoute>} />
      <Route path="/admin/prospect/:prospectId" element={<ProtectedRoute><ProspectDetailPage /></ProtectedRoute>} />
      <Route path="/admin/qualiopi" element={<ProtectedRoute><QualiopiPage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin/observability" element={<ProtectedRoute><SuperadminRoute><ObservabilityPage /></SuperadminRoute></ProtectedRoute>} />
      <Route path="/admin/outils" element={<ProtectedRoute><OutilsPage /></ProtectedRoute>} />
      <Route path="/admin/import" element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
      <Route path="/admin/recherche-ia" element={<Navigate to="/admin/agent-ia" replace />} />
      <Route path="/admin/conception-docs" element={<Navigate to="/admin/agent-ia" replace />} />
      <Route path="/admin/depenses" element={<ProtectedRoute><DepensesPage /></ProtectedRoute>} />
      <Route path="/admin/encaissements" element={<ProtectedRoute><EncaissementsPage /></ProtectedRoute>} />
      <Route path="/admin/agent-ia" element={<ProtectedRoute><AgentIAPage /></ProtectedRoute>} />
      <Route path="/admin/tresorerie" element={<ProtectedRoute><TresoreriePage /></ProtectedRoute>} />
      <Route path="/admin/holding" element={<ProtectedRoute><HoldingPage /></ProtectedRoute>} />
      <Route path="/admin/societes" element={<ProtectedRoute><SocietesPage /></ProtectedRoute>} />
      <Route path="/admin/societes/:societe_id/builder" element={<ProtectedRoute><AdminRouteWrapper><SocieteBuilderPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/admin/partenaires" element={<ProtectedRoute><AdminRouteWrapper><PartnersAdminPage /></AdminRouteWrapper></ProtectedRoute>} />
      <Route path="/partner/accept/:token" element={<PartnerAcceptPage />} />
      <Route path="/partner" element={<ProtectedRoute><PartnerLayout /></ProtectedRoute>}>
        <Route index element={<PartnerHome />} />
        <Route path="commandes" element={<PartnerOrders />} />
        <Route path="factures" element={<PartnerInvoices />} />
        <Route path="catalogue" element={<PartnerCatalogue />} />
        <Route path="formation" element={<PartnerLms />} />
        <Route path="missions" element={<PartnerMissions />} />
        <Route path="leads" element={<PartnerLeads />} />
        <Route path="commissions" element={<PartnerCommissions />} />
        <Route path="messages" element={<PartnerMessages />} />
        <Route path="documents" element={<PartnerDocuments />} />
      </Route>
      <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/mail-agent" element={<Navigate to="/admin/agent-ia" replace />} />
      <Route path="/admin/agent-gestion" element={<Navigate to="/admin/agent-ia" replace />} />
      <Route path="/tenant/team" element={<ProtectedRoute><TenantTeamPage /></ProtectedRoute>} />
      <Route path="/tenant/postes" element={<ProtectedRoute><PostesPage /></ProtectedRoute>} />
      <Route path="/tenant/team/access" element={<ProtectedRoute><TeamAccessPage /></ProtectedRoute>} />
      <Route path="/tenant/team/autorisations-societes" element={<ProtectedRoute><SocietePermissionsPage /></ProtectedRoute>} />
      <Route path="/tenant/team/:userId" element={<ProtectedRoute><MemberProfilePage /></ProtectedRoute>} />
      {/* Iter 247 — Audit log des permissions */}
      <Route path="/admin/audit/permissions" element={<ProtectedRoute><PermissionsAuditPage /></ProtectedRoute>} />
      {/* Iter 248 — Aircall Live Translator FR ↔ EN */}
      <Route path="/admin/aircall/translator" element={<ProtectedRoute><AircallTranslatorPage /></ProtectedRoute>} />
      <Route path="/admin/commercial-panel" element={<ProtectedRoute><CommercialPanelPage /></ProtectedRoute>} />
      <Route path="/admin/objection-cookbook" element={<ProtectedRoute><ObjectionCookbookPage /></ProtectedRoute>} />
      <Route path="/admin/translator/manager-stats" element={<ProtectedRoute><ManagerCoachStatsPage /></ProtectedRoute>} />
      <Route path="/tenant/settings" element={<ProtectedRoute><TenantSettingsPage /></ProtectedRoute>} />
      <Route path="/tenant/branding" element={<ProtectedRoute><BrandingSettingsPage /></ProtectedRoute>} />
      <Route path="/tenant/integrations" element={<Navigate to="/admin/integrations-health" replace />} />
      {/* Marketing */}
      <Route path="/admin/marketing" element={<ProtectedRoute><MarketingHubPage /></ProtectedRoute>} />
      <Route path="/admin/marketing/social" element={<ProtectedRoute><MarketingPage /></ProtectedRoute>} />
      <Route path="/admin/marketing/mailing" element={<ProtectedRoute><MailingPage /></ProtectedRoute>} />
      <Route path="/admin/marketing/studio" element={<ProtectedRoute><MarketingStudioPage /></ProtectedRoute>} />
      <Route path="/admin/marketing/google" element={<ProtectedRoute><MarketingGooglePage /></ProtectedRoute>} />
      <Route path="/admin/marketing/affiliation" element={<ProtectedRoute><AffiliatePage /></ProtectedRoute>} />
      <Route path="/aide" element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
      <Route path="/admin/aide" element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      {/* Iter 335 — App « Blitzz Salarié » (PWA séparée, hors ligne, push) */}
      <Route path="/salarie" element={<SalarieLayout><SalarieHomePage /></SalarieLayout>} />
      <Route path="/salarie/notifications" element={<SalarieLayout><NotificationsPage /></SalarieLayout>} />
      <Route path="/salarie/messages" element={<SalarieLayout><MessagesPage /></SalarieLayout>} />
      <Route path="/salarie/mon-espace" element={<SalarieLayout><SalarieEspacePage /></SalarieLayout>} />
      <Route path="/salarie/taches" element={<SalarieLayout><PersonalSpacePage defaultTab="tasks" /></SalarieLayout>} />
      <Route path="/salarie/depenses" element={<SalarieLayout><PersonalSpacePage defaultTab="expenses" /></SalarieLayout>} />
      <Route path="/salarie/equipement" element={<SalarieLayout><PersonalEquipmentsPage /></SalarieLayout>} />
      <Route path="/salarie/bulletins" element={<SalarieLayout><MyPayslipsPage /></SalarieLayout>} />
      <Route path="/salarie/documents" element={<SalarieLayout><CloudDocumentsPage initialScope="personal" hideScopeTabs /></SalarieLayout>} />
      <Route path="/salarie/pointages" element={<SalarieLayout><MyPunchPage /></SalarieLayout>} />
      <Route path="/salarie/reglages" element={<SalarieLayout><SalarieSettingsPage /></SalarieLayout>} />
      <Route path="/salarie/app" element={<SalarieLayout><AppInstallPage app="salarie" /></SalarieLayout>} />
      {/* Client-side routes for comptabilité modules */}
      <Route path="/social" element={<ProtectedRoute><SocialPage /></ProtectedRoute>} />
      <Route path="/club" element={<ProtectedRoute><ClubPage /></ProtectedRoute>} />
      <Route path="/admin/fiche-renseignement/:userId" element={<ProtectedRoute><FicheRenseignementPage /></ProtectedRoute>} />
      {/* Public routes (no auth) */}
      <Route path="/form/:qid" element={<PublicQuestionnairePage />} />
      <Route path="/" element={<Navigate to="/login" replace />} />
      {/* 404 fallback : authenticated users see a friendly NotFound,
          unauthenticated users get redirected to /login (preserving previous behaviour). */}
      <Route path="*" element={<NotFoundFallback />} />
    </Routes>
    </ErrorBoundary>
  );
}

/** Routes-aware 404 : redirects to /login if no session, else renders NotFoundPage. */
function NotFoundFallback() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <NotFoundPage />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LiteModeProvider>
        <AuthProvider>
          <UserLookupProvider>
          <SocieteProvider>
            <SecretModeProvider>
              <BrandThemeApplier />
              <BrandedApp />
            </SecretModeProvider>
          </SocieteProvider>
          </UserLookupProvider>
        </AuthProvider>
        </LiteModeProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function BrandedApp() {
  // Fetch and apply tenant branding. Slug is resolved from (in priority):
  //   1) ?t=<slug> URL param  2) subdomain  3) authenticated user's tenant_id
  const { user } = useAuth();
  useBranding(user?.tenant_id || undefined);
  return (
    <>
      <AppRouter />
      <PWAPrompt />
      <Toaster position="top-right" richColors />
    </>
  );
}

export default App;

